import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { combineLatest, of } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { StatementsFacade } from '@app/modules/businesses/services/statements.facade';
import { UpsellService } from '@app/shared/+state/shared/upsell.service';

@Component({
  standalone: false,
  selector: 'sbnb-statements-properties',
  templateUrl: './statements-properties.component.html',
  styleUrls: ['./statements-properties.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatementsPropertiesComponent implements OnInit {
  public propertiesData$ = this.statementsService.getPropertiesForSetup();
  public columns = ['name', 'commissionRate', 'status', 'actions'];
  public canAddProperty$ = combineLatest([
    this.statementsService.propertiesState$,
    this.statementsService.ownerStatementEntitlement$,
  ]).pipe(
    filter(([properties, entitlement]) => Boolean(properties.data?.length) && Boolean(entitlement)),
    map(([properties, entitlement]) => {
      const availableProperties = properties?.data?.filter((property) => property.owners.length === 0);

      if (!availableProperties?.length) {
        return {
          canAdd: false,
          message: 'All properties have been assigned to an owner',
        };
      }

      if (!entitlement.unlimited && Number(entitlement?.current_value) >= Number(entitlement?.value)) {
        return {
          canAdd: false,
          message: 'You have reached the limit of owner statement properties you can trial.',
        };
      }

      return { canAdd: true };
    })
  );

  public accountingConnectionProperties$ = this.upsellService.accountingEnabled$.pipe(
    filter((enabled) => Boolean(enabled)),
    switchMap(() => this.statementsService.connectionsState$),
    switchMap((connections) => {
      if (!connections?.data?.[0]?.id) {
        return of(null);
      }
      return this.statementsService.getCurrentAccountMapping(connections.data[0].id);
    }),
    map((currentMapping) => new Set(currentMapping?.properties?.map((properties) => properties.id))),
    shareReplay(1)
  );

  constructor(
    private readonly statementsService: StatementsFacade,
    private readonly upsellService: UpsellService
  ) {
    this.statementsService.getProperties();
    this.statementsService.listConnections();
  }

  ngOnInit(): void {}
}
